import {cloneDeep} from 'lodash';
import {IntlShape} from 'react-intl';

import {idFromGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';
import Util from '../../../forecast-app/shared/util/util';
import {BaselinePDFItemType, BaselinePDFPhaseType, BaselinePDFProjectType} from './BaselinePDFTypes';

// Mocked relay type since Baseline is not TS. Replace with generated relay types when Baseline is converted to TS.
type PhaseRelay = {
	node: {
		id: string;
		name?: string;
		phaseBaselineRoles?: {edges: {node: PhaseBaselineRoleRelay}[]};
		phaseBaselineExpenses?: {edges: {node: PhaseBaselineExpenseRelay}[]};
	};
};

type PhaseBaselineRoleRelay = {
	id: string;
	role?: {
		name?: string;
	};
	baselineMinutes?: number;
};

type PhaseBaselineExpenseRelay = {
	id: string;
	expenseCategory?: {
		name?: string;
	};
};

type PhaseFinancials = {
	totalBaselineRevenue: number;
	roles: Map<number, RoleFinancials>;
	expenses: Map<number, ExpenseFinancials>;
};

type RoleFinancials = {
	baselineRatePerHour: number;
	baselineRevenue: number;
};

type ExpenseFinancials = {
	baselineRevenue: number;
};

type FormatterBase = {
	currencySymbol: string;
	intl: IntlShape;
};

interface FormatPhaseBaselineRoleInput extends FormatterBase {
	phaseBaselineRole: PhaseBaselineRoleRelay;
	roleFinancials?: RoleFinancials;
}
const formatPhaseBaselineRole = ({
	phaseBaselineRole,
	roleFinancials,
	currencySymbol,
	intl,
}: FormatPhaseBaselineRoleInput): BaselinePDFItemType => {
	const baselineRatePerHour = roleFinancials?.baselineRatePerHour ?? 0;
	const baselineRevenue = roleFinancials?.baselineRevenue ?? 0;
	return {
		id: phaseBaselineRole.id,
		name: phaseBaselineRole.role?.name ?? '',
		hours: phaseBaselineRole.baselineMinutes ? String(phaseBaselineRole.baselineMinutes / 60) : '0',
		hourlyRate: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRatePerHour, {format: 'always_two_decimal'})
		),
		totalPrice: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRevenue, {format: 'always_two_decimal'})
		),
	};
};

interface FormatPhaseBaselineExpenseInput extends FormatterBase {
	phaseBaselineExpense: PhaseBaselineExpenseRelay;
	expenseFinancials?: ExpenseFinancials;
}
const formatPhaseBaselineExpense = ({
	phaseBaselineExpense,
	expenseFinancials,
	currencySymbol,
	intl,
}: FormatPhaseBaselineExpenseInput): BaselinePDFItemType => {
	const baselineRevenue = expenseFinancials?.baselineRevenue ?? 0;
	return {
		id: phaseBaselineExpense.id,
		name: `Expense: ${phaseBaselineExpense.expenseCategory?.name ?? ''}`,
		hours: '-',
		hourlyRate: '-',
		totalPrice: Util.getFormattedCurrencyValue(
			currencySymbol,
			intl.formatNumber(baselineRevenue, {format: 'always_two_decimal'})
		),
	};
};

interface FormatPhaseBaselineItemsInput extends FormatterBase {
	phaseBaselineRoles?: {node: PhaseBaselineRoleRelay}[];
	phaseBaselineExpenses?: {node: PhaseBaselineExpenseRelay}[];
	phaseFinancialNumbers?: PhaseFinancials;
}
const formatPhaseBaselineItems = ({
	phaseBaselineRoles,
	phaseBaselineExpenses,
	phaseFinancialNumbers,
	currencySymbol,
	intl,
}: FormatPhaseBaselineItemsInput): BaselinePDFItemType[] => {
	const phaseBaselineRoleItems = phaseBaselineRoles
		? phaseBaselineRoles.map(phaseBaselineRoleNode => {
				const phaseBaselineRole = phaseBaselineRoleNode.node;
				const phaseBaselineRoleId = idFromGlobalId(phaseBaselineRole.id);
				const roleFinancials = phaseFinancialNumbers?.roles.get(phaseBaselineRoleId);
				return formatPhaseBaselineRole({phaseBaselineRole, roleFinancials, currencySymbol, intl});
		  })
		: [];
	const phaseBaselineExpenseItems = phaseBaselineExpenses
		? phaseBaselineExpenses.map(phaseBaselineExpenseNode => {
				const phaseBaselineExpense = phaseBaselineExpenseNode.node;
				const phaseBaselineExpenseId = idFromGlobalId(phaseBaselineExpense.id);
				const expenseFinancials = phaseFinancialNumbers?.expenses.get(phaseBaselineExpenseId);
				return formatPhaseBaselineExpense({phaseBaselineExpense, expenseFinancials, currencySymbol, intl});
		  })
		: [];

	return [...phaseBaselineRoleItems, ...phaseBaselineExpenseItems];
};

interface FormatPhasesInput extends FormatterBase {
	phases: PhaseRelay[];
	phaseSortOrder: string[];
	groupedFinancialNumbers: Map<number, PhaseFinancials>;
}
export const formatPhases = ({
	phases,
	groupedFinancialNumbers,
	phaseSortOrder,
	currencySymbol,
	intl,
}: FormatPhasesInput): BaselinePDFPhaseType[] => {
	return cloneDeep(phases)
		.sort((a, b) => phaseSortOrder.indexOf(a.node.id) - phaseSortOrder.indexOf(b.node.id))
		.map(phaseNode => {
			const phase = phaseNode.node;
			const phaseId = idFromGlobalId(phase.id);
			const phaseFinancialNumbers = groupedFinancialNumbers.get(phaseId);
			const phaseTotal = phaseFinancialNumbers?.totalBaselineRevenue ?? 0;
			return {
				id: phase.id,
				name: phase.name ?? '',
				total: Util.getFormattedCurrencyValue(
					currencySymbol,
					intl.formatNumber(phaseTotal, {format: 'always_two_decimal'})
				),
				items: formatPhaseBaselineItems({
					phaseBaselineRoles: phase.phaseBaselineRoles?.edges,
					phaseBaselineExpenses: phase.phaseBaselineExpenses?.edges,
					phaseFinancialNumbers,
					currencySymbol,
					intl,
				}),
			};
		});
};

interface FormatProjectInput extends FormatterBase {
	project: {name: string};
	totalFinancialNumbers?: {totalBaselineRevenue?: number};
}
export const formatProject = ({
	project,
	totalFinancialNumbers,
	currencySymbol,
	intl,
}: FormatProjectInput): BaselinePDFProjectType => {
	const projectTotal = totalFinancialNumbers?.totalBaselineRevenue ?? 0;
	return {
		name: project.name,
		total: Util.getFormattedCurrencyValue(currencySymbol, intl.formatNumber(projectTotal, {format: 'always_two_decimal'})),
	};
};
